import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { SButton, SIcon, SModal, type SIconName } from '@chargebee/sting-react';
import { getPlayTypeFromRoute } from '@bbkAdminComponents/unified-plays/utils/play-type-utils';
import { selectAppId } from '@bbkAdminRedux/app/selectors';
import {
  DEFAULT_BRAND_ID,
  useGetSelectedBrand,
} from '@bbkAdminRedux/rtkq/brands.slice';
import { PlayPresentation, PlayType } from '@bbkAdminRedux/rtkq/play.slice';
import { encodeObjectForQuery } from '@bbkAdminRedux/rtkq/rtkq-utils';
import { AuthorizedPaths } from '@bbkAdminRouter/router-paths';
import { cs } from '@bbkAdminUtils/css-utils';
import { useUrl } from '@bbkAdminUtils/nav';
import { useHistory } from '@bbkShared/react-router';

enum PlayTypeOption {
  PRICING_TABLE = 'pricing-table',
  CANCEL_PAGE = 'cancel-page',
  IN_APP = 'in-app',
}

type CardOption = {
  type: string;
  icon: SIconName;
  title: string;
  description: string;
};

const IN_APP_OPTION: CardOption = {
  type: PlayTypeOption.IN_APP,
  icon: 'badge-percent',
  title: 'In-app offer',
  description:
    'Show personalized contextual offers to your subscribers using Chargebee.js or through our API for fully custom experiences.',
};

const RETENTION_PLAY_OPTIONS: CardOption[] = [
  {
    type: PlayTypeOption.CANCEL_PAGE,
    icon: 'user-x',
    title: 'Cancel page',
    description:
      'Create personalized cancellation journeys and present targeted retention offers to subscribers at the moment of cancel. Requires installing Chargebee.js.',
  },
  IN_APP_OPTION,
];

const NON_RETENTION_PLAY_OPTIONS: CardOption[] = [
  {
    type: PlayTypeOption.PRICING_TABLE,
    icon: 'grid-3x2',
    title: 'Pricing table',
    description:
      'Deploy personalized pricing tables with dynamic plan purchase and upgrade options.',
  },
  IN_APP_OPTION,
];

const PRESENTATION_OPTIONS: CardOption[] = [
  {
    type: PlayPresentation.MODAL,
    icon: 'square-stop',
    title: 'Modal',
    description: 'Full-screen overlay that captures user attention.',
  },
  {
    type: PlayPresentation.POP_UP,
    icon: 'vote',
    title: 'Pop-up',
    description: 'Small contextual message that appears over content.',
  },
  {
    type: PlayPresentation.BANNER,
    icon: 'panel-top',
    title: 'Banner',
    description: 'Horizontal bar at the top or bottom of the screen.',
  },
];

const SelectionCard: React.FC<{
  icon: SIconName;
  title: string;
  description: string;
  isSelected: boolean;
  onClick: () => void;
}> = ({ icon, title, description, isSelected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={cs(
      'tw-flex tw-cursor-pointer tw-flex-col tw-items-start tw-rounded-lg tw-border tw-bg-white tw-px-5 tw-pb-5 tw-pt-4 tw-text-left tw-transition-all focus:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-cbrprimary-600 focus-visible:tw-ring-offset-4',
      {
        'tw-border-cbrprimary-500 !tw-bg-cbrprimary-50 tw-shadow-sm':
          isSelected,
        'tw-border-neutral-200 hover:tw-border-cbrprimary-500 hover:tw-shadow-sm':
          !isSelected,
      }
    )}
  >
    <SIcon
      name={icon}
      size={28}
      className={cs('tw-mb-3', {
        'tw-text-cbrprimary-600': isSelected,
        'tw-text-neutral-700': !isSelected,
      })}
    />
    <h3
      className={cs('tw-m-0 tw-mb-1.5 tw-text-base tw-font-semibold', {
        'tw-text-cbrprimary-600': isSelected,
        'tw-text-neutral-900': !isSelected,
      })}
    >
      {title}
    </h3>
    <p className="tw-m-0 tw-text-sm tw-leading-relaxed tw-text-neutral-500">
      {description}
    </p>
  </button>
);

const StepIndicator: React.FC<{
  currentStep: number;
  totalSteps: number;
}> = ({ currentStep, totalSteps }) => (
  <div className="tw-flex tw-gap-1.5">
    {Array.from({ length: totalSteps }, (_, i) => (
      <div
        key={i}
        className={cs('tw-h-1 tw-w-10 tw-rounded-full', {
          'tw-bg-cbrprimary-500': i < currentStep,
          'tw-bg-neutral-200': i >= currentStep,
        })}
      />
    ))}
  </div>
);

type AdditionalNavigationState = {
  riskCategory?: string;
  name?: string;
  returnTab?: string;
  returnUrl?: string;
  [key: string]: unknown;
};

type CreatePlayModalProps = {
  isOpen: boolean;
  onClose: (open: boolean) => void;
  routePlayType: string | undefined;
  additionalState?: AdditionalNavigationState;
};

export const CreatePlayModal: React.FC<CreatePlayModalProps> = ({
  isOpen,
  onClose,
  routePlayType,
  additionalState,
}) => {
  const [step, setStep] = useState(1);
  const [selectedPlayType, setSelectedPlayType] =
    useState<PlayTypeOption | null>(null);
  const [selectedPresentationType, setSelectedPresentationType] =
    useState<PlayPresentation | null>(null);
  const history = useHistory();
  const createUrl = useUrl();

  const appKey = useSelector(selectAppId);
  const playType = getPlayTypeFromRoute(routePlayType);
  const isRetention = playType === PlayType.RETENTION;
  const brandId = useGetSelectedBrand(appKey) ?? DEFAULT_BRAND_ID;

  const playTypeOptions = isRetention
    ? RETENTION_PLAY_OPTIONS
    : NON_RETENTION_PLAY_OPTIONS;

  const handleClose = useCallback(
    (open: boolean) => {
      if (!open) {
        setStep(1);
        setSelectedPlayType(null);
        setSelectedPresentationType(null);
      }
      onClose(open);
    },
    [onClose]
  );

  const handleProceed = useCallback(() => {
    const getNavigationUrl = () => {
      const baseUrl = createUrl(AuthorizedPaths.unifiedPlaysNew);
      if (additionalState?.returnUrl) {
        return (
          baseUrl +
          encodeObjectForQuery({ returnUrl: additionalState.returnUrl })
        );
      }
      return baseUrl;
    };

    const getNavigationState = (presentationType: PlayPresentation) => ({
      playType: getPlayTypeFromRoute(routePlayType),
      presentationType,
      brandId,
      ...additionalState,
    });

    if (selectedPlayType === PlayTypeOption.PRICING_TABLE) {
      history.push(
        getNavigationUrl(),
        getNavigationState(PlayPresentation.PRICING_PAGE)
      );
    } else if (selectedPlayType === PlayTypeOption.CANCEL_PAGE) {
      history.push(
        getNavigationUrl(),
        getNavigationState(PlayPresentation.CANCEL_PAGE)
      );
    } else if (
      selectedPlayType === PlayTypeOption.IN_APP &&
      selectedPresentationType
    ) {
      history.push(
        getNavigationUrl(),
        getNavigationState(selectedPresentationType)
      );
    }
    handleClose(false);
  }, [
    selectedPlayType,
    selectedPresentationType,
    history,
    createUrl,
    routePlayType,
    handleClose,
    brandId,
    additionalState,
  ]);

  const handlePlayTypeSelect = useCallback((type: PlayTypeOption) => {
    setSelectedPlayType(type);
    if (type === PlayTypeOption.IN_APP) {
      setSelectedPresentationType(PlayPresentation.MODAL);
    } else {
      setSelectedPresentationType(null);
    }
  }, []);

  const handleNextOrProceed = useCallback(() => {
    if (selectedPlayType === PlayTypeOption.IN_APP) {
      setStep(2);
    } else {
      handleProceed();
    }
  }, [selectedPlayType, handleProceed]);

  const isInAppSelected = selectedPlayType === PlayTypeOption.IN_APP;
  const showSteps = isInAppSelected;

  const playTypeLabel =
    playType === PlayType.RETENTION
      ? 'retention'
      : playType === PlayType.UPSELL
        ? 'expansion'
        : 'acquisition';

  const playTypeDescription =
    playType === PlayType.RETENTION
      ? 'Keep customers engaged and prevent churn'
      : playType === PlayType.UPSELL
        ? 'Grow revenue from existing customers'
        : 'Convert prospects into paying customers.';

  return (
    <SModal open={isOpen} onOpenChange={handleClose}>
      <SModal.Content size="regular" padding="large">
        <SModal.Header showCloseButton className="!tw-border-b-0 tw-pb-md">
          <SModal.Title className="tw-mb-2 tw-ml-4 tw-mt-1 !tw-text-xl tw-font-semibold tw-text-neutral-900">
            {step === 1
              ? `Choose ${playTypeLabel} play type`
              : 'Choose in-app offer type:'}
          </SModal.Title>
          <SModal.Description className="tw-ml-4 tw-text-base tw-text-neutral-600">
            {step === 1
              ? playTypeDescription
              : 'Select the type of offer you want to display.'}
          </SModal.Description>
        </SModal.Header>
        <SModal.Body className="tw-mb-4 !tw-px-md !tw-pb-lg">
          {step === 1 ? (
            <div className="tw-mt-4 tw-grid tw-grid-cols-2 tw-gap-4">
              {playTypeOptions.map((option) => (
                <SelectionCard
                  key={option.type}
                  icon={option.icon}
                  title={option.title}
                  description={option.description}
                  isSelected={selectedPlayType === option.type}
                  onClick={() =>
                    handlePlayTypeSelect(option.type as PlayTypeOption)
                  }
                />
              ))}
            </div>
          ) : (
            <div className="tw-grid tw-grid-cols-3 tw-gap-4">
              {PRESENTATION_OPTIONS.map((option) => (
                <SelectionCard
                  key={option.type}
                  icon={option.icon}
                  title={option.title}
                  description={option.description}
                  isSelected={selectedPresentationType === option.type}
                  onClick={() =>
                    setSelectedPresentationType(option.type as PlayPresentation)
                  }
                />
              ))}
            </div>
          )}
        </SModal.Body>
        <SModal.Footer className="!tw-flex-row !tw-items-center">
          <div className="tw-mr-auto tw-pl-5">
            {showSteps && <StepIndicator currentStep={step} totalSteps={2} />}
          </div>
          <div className="tw-flex tw-gap-2">
            {step === 2 && (
              <SButton
                variant="neutral"
                size="large"
                onClick={() => setStep(1)}
              >
                Back
              </SButton>
            )}
            <SButton
              onClick={step === 1 ? handleNextOrProceed : handleProceed}
              disabled={
                step === 1
                  ? selectedPlayType === null
                  : selectedPresentationType === null
              }
              icon={<SIcon name="arrow-right" size={16} />}
              iconPosition="right"
              size="large"
            >
              {step === 1 && isInAppSelected ? 'Next' : 'Proceed'}
            </SButton>
          </div>
        </SModal.Footer>
      </SModal.Content>
    </SModal>
  );
};
